import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { LocalStorageService } from '../services/local-storage.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  private storageService = inject(LocalStorageService);
  private authService = inject(AuthService);
  private router = inject(Router);

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return !payload.exp || Date.now() / 1000 > payload.exp;
    } catch {
      return true;
    }
  }

  private addAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', 'bearer ' + token),
    });
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!request.headers.has('Accept')) {
      request = request.clone({
        headers: request.headers.set('Accept', 'application/json'),
      });
    }

    if (
      request.headers.has('Authorization') ||
      request.url.includes('/authentication/refresh')
    ) {
      return next.handle(request);
    }

    const token = this.storageService.getFromStorage('token');

    if (token && this.isTokenExpired(token)) {
      return this.handleRefresh(request, next);
    }

    if (token) {
      request = this.addAuthHeader(request, token);
    }

    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handleRefresh(request, next);
        }

        if (error.status === 403) {
          const currentToken = this.storageService.getFromStorage('token');
          if (!currentToken || this.isTokenExpired(currentToken)) {
            return this.handleRefresh(request, next);
          }
        }

        return throwError(() => error);
      })
    );
  }

  private handleRefresh(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const refreshToken = this.storageService.getFromStorage('refreshToken');

    if (!refreshToken) {
      this.storageService.removeFromStorage('token');
      this.router.navigate(['/auth/login']);
      return throwError(
        () => new HttpErrorResponse({ status: 401, statusText: 'Session expired' })
      );
    }

    return this.authService.refreshSession().pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.storageService.removeFromStorage('token');
          this.storageService.removeFromStorage('refreshToken');
          this.router.navigate(['/auth/login']);
        }
        return throwError(() => err);
      }),
      switchMap((token) => next.handle(this.addAuthHeader(request, token)))
    );
  }
}
